import { useEffect, useRef } from 'react';
import { watchLevel } from '../lib/audioLevel.ts';

/** Ab diesem Ausschlag gilt eine Spur als „Ton kommt an" statt als still. */
const AUDIBLE = 0.08;

/**
 * Kleiner Mikrofon-Pegel: ein Balken, der zeigt, ob in einer Tonspur gerade
 * etwas ankommt.
 *
 * Der Balken wird direkt am DOM nachgezogen und nicht ueber React-Zustand --
 * zehn Messungen pro Sekunde in jeder Vorschau der Regie waeren sonst zehn
 * Neuzeichnungen der ganzen Kachel.
 *
 * `off` heisst: die Regie hat das Mikrofon dieser Quelle abgeschaltet. Dann
 * misst die Anzeige nicht, sondern sagt das -- Stille aus Absicht ist etwas
 * anderes als Stille aus einem Fehler.
 */
export function LevelMeter({
  stream,
  off = false,
  className = '',
}: {
  stream: MediaStream | null | undefined;
  off?: boolean;
  className?: string;
}) {
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const fillRef = useRef<HTMLSpanElement | null>(null);
  const hasAudio = Boolean(stream && stream.getAudioTracks().length > 0);

  useEffect(() => {
    if (!stream || !hasAudio || off) return;
    const stop = watchLevel(stream, (level) => {
      const root = rootRef.current;
      const fill = fillRef.current;
      if (!root || !fill) return;
      root.dataset.state = level === null ? 'idle' : level >= AUDIBLE ? 'live' : 'quiet';
      fill.style.clipPath = `inset(0 ${Math.round((1 - (level ?? 0)) * 100)}% 0 0)`;
    });
    return () => {
      stop();
      if (fillRef.current) fillRef.current.style.clipPath = '';
    };
  }, [stream, hasAudio, off]);

  if (!stream) return null;
  const state = !hasAudio ? 'none' : off ? 'off' : 'idle';
  const title = !hasAudio
    ? 'Kein Mikrofon in dieser Quelle'
    : off
      ? 'Mikrofon von der Regie abgeschaltet'
      : 'Mikrofon-Pegel';

  return (
    <span ref={rootRef} className={`level ${className}`} data-state={state} title={title}>
      <span className="level__icon" aria-hidden>
        {state === 'none' || state === 'off' ? '🔇' : '🎤'}
      </span>
      <span className="level__track" aria-hidden>
        <span className="level__fill" ref={fillRef} />
      </span>
    </span>
  );
}
